import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useCarteraStatement } from "@/lib/api/cartera.queries";
import { useActiveCompany } from "@/lib/company-context";
import type {
  CarteraSide,
  CarteraStatementRow,
  CarteraStatementRowKind,
} from "@/lib/api/cartera";
import {
  STATEMENT_SOURCE_TO,
  statementRowSourceTarget,
  type StatementSourceTarget,
} from "@/lib/cartera-statement-source";

type StatementSearch = {
  side: CarteraSide;
  from?: string;
  to?: string;
};

export const Route = createFileRoute(
  "/companies/$companyId/cartera/$terceroId",
)({
  validateSearch: (search: Record<string, unknown>): StatementSearch => ({
    side: search.side === "payables" ? "payables" : "receivables",
    ...(typeof search.from === "string" && search.from
      ? { from: search.from }
      : {}),
    ...(typeof search.to === "string" && search.to ? { to: search.to } : {}),
  }),
  component: CarteraStatementPage,
});

const KIND_BADGE: Record<
  CarteraStatementRowKind,
  "default" | "secondary" | "muted" | "success" | "destructive" | "warning" | "outline"
> = {
  invoice: "outline",
  support_document: "muted",
  credit_note: "warning",
  debit_note: "secondary",
  payment: "success",
};

const SOURCE_LINK_CLASS = "font-mono text-sm text-primary hover:underline";

function StatementSourceLink({
  companyId,
  side,
  row,
}: {
  companyId: string;
  side: CarteraSide;
  row: CarteraStatementRow;
}) {
  const { t } = useTranslation();
  const target = statementRowSourceTarget(side, row);
  const label = (
    <>
      {row.documentRef}
      {row.paymentNumber !== null ? (
        <span className="ml-1 text-xs text-muted-foreground">
          ({side === "receivables" ? "RC" : "CE"}-{row.paymentNumber})
        </span>
      ) : null}
    </>
  );
  if (!target) {
    return <span className="font-mono text-sm">{label}</span>;
  }
  return (
    <SourceDocumentLink
      companyId={companyId}
      target={target}
      ariaLabel={t("cartera.statement.open_source", { ref: row.documentRef })}
    >
      {label}
    </SourceDocumentLink>
  );
}

function SourceDocumentLink({
  companyId,
  target,
  ariaLabel,
  children,
}: {
  companyId: string;
  target: StatementSourceTarget;
  ariaLabel: string;
  children: ReactNode;
}) {
  if (target.table === "sales_invoices") {
    return (
      <Link
        to={STATEMENT_SOURCE_TO.sales_invoices}
        params={{ companyId, invoiceId: target.invoiceId }}
        className={SOURCE_LINK_CLASS}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }
  if (target.table === "purchase_invoices") {
    return (
      <Link
        to={STATEMENT_SOURCE_TO.purchase_invoices}
        params={{ companyId, invoiceId: target.invoiceId }}
        className={SOURCE_LINK_CLASS}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      to={STATEMENT_SOURCE_TO.support_documents}
      params={{ companyId, documentId: target.documentId }}
      className={SOURCE_LINK_CLASS}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

function CarteraStatementPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CarteraStatementContent />
      </AppShell>
    </RequireAuth>
  );
}

function CarteraStatementContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, terceroId } = Route.useParams();
  const search = Route.useSearch();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [from, setFrom] = useState(search.from ?? "");
  const [to, setTo] = useState(search.to ?? "");

  const statementQuery = useCarteraStatement(
    companyId,
    search.side,
    terceroId,
    {
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    },
  );

  if (statementQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (statementQuery.isError || !statementQuery.data) {
    return (
      <ErrorState
        title={t("cartera.statement.error_title")}
        description={t("cartera.statement.error_body")}
        error={statementQuery.error ?? undefined}
        onRetry={() => void statementQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const statement = statementQuery.data;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/cartera"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("cartera.statement.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {statement.terceroName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {search.side === "receivables"
              ? t("cartera.statement.subtitle_cxc")
              : t("cartera.statement.subtitle_cxp")}
          </p>
        </div>
        <div className="flex gap-3">
          <div>
            <Label htmlFor="statement-from">{t("cartera.statement.from")}</Label>
            <Input
              id="statement-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <Label htmlFor="statement-to">{t("cartera.statement.to")}</Label>
            <Input
              id="statement-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      </div>

      {/* Closing balance headline */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">
              {t("cartera.statement.opening")}
            </p>
            <p className="font-mono text-lg font-semibold">
              {formatCop(statement.openingBalance)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">
              {t("cartera.statement.movements", {
                debits: formatCop(statement.totals.debits),
                credits: formatCop(statement.totals.credits),
              })}
            </p>
            <p className="font-mono text-lg font-semibold">
              {statement.rowCount}
            </p>
          </CardContent>
        </Card>
        <Card className="border-primary/40">
          <CardContent className="pt-6">
            <p className="text-xs text-muted-foreground">
              {t("cartera.statement.closing", { to: statement.to })}
            </p>
            <p className="font-mono text-lg font-semibold">
              {formatCop(statement.closingBalance)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("cartera.statement.table_title")}
          </CardTitle>
          <CardDescription>
            {statement.from
              ? t("cartera.statement.range", {
                  from: statement.from,
                  to: statement.to,
                })
              : t("cartera.statement.until", { to: statement.to })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {statement.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("cartera.statement.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cartera.statement.col_date")}</TableHead>
                  <TableHead>{t("cartera.statement.col_kind")}</TableHead>
                  <TableHead>{t("cartera.statement.col_ref")}</TableHead>
                  <TableHead className="text-right">
                    {t("cartera.statement.col_debit")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cartera.statement.col_credit")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("cartera.statement.col_balance")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {statement.from ? (
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={5} className="text-sm text-muted-foreground">
                      {t("cartera.statement.opening_row")}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(statement.openingBalance)}
                    </TableCell>
                  </TableRow>
                ) : null}
                {statement.rows.map((row, idx) => (
                  <TableRow key={`${row.kind}-${row.invoiceId}-${idx}`}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {row.date}
                    </TableCell>
                    <TableCell>
                      <Badge variant={KIND_BADGE[row.kind]} className="text-[10px]">
                        {t(`cartera.kind.${row.kind}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <StatementSourceLink
                        companyId={companyId}
                        side={search.side}
                        row={row}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.debit > 0 ? formatCop(row.debit) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {row.credit > 0 ? formatCop(row.credit) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-medium">
                      {formatCop(row.balance)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell colSpan={3}>
                    {t("cartera.statement.totals_row")}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(statement.totals.debits)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(statement.totals.credits)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold">
                    {formatCop(statement.closingBalance)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
